import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { IMAGES } from '../constants/images';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [subsidiesOpen, setSubsidiesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const subsidySchemes = [
    { name: 'RIPS 2024', path: '/subsidies/rips-2024', description: 'Rajasthan Investment Promotion Scheme' },
    { name: 'PMEGP', path: '/subsidies/pmegp', description: 'PM Employment Generation Programme' },
    { name: 'VYUPY', path: '/subsidies/vyupy', description: 'Vishwakarma Yuva Udyami Protsahan' },
    { name: 'MSME Policy 2024', path: '/subsidies/msme-policy-2024', description: 'Rajasthan MSME Incentives' },
    { name: 'Export Policy 2024', path: '/subsidies/export-policy-2024', description: 'Export Promotion Policy' },
    { name: 'Logistics Policy 2025', path: '/subsidies/logistics-policy-2025', description: 'Logistics Infrastructure Support' },
    { name: 'ODOP Policy 2024', path: '/subsidies/odop-policy-2024', description: 'One District One Product' },
  ];

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Subsidies', path: '/subsidies', hasDropdown: true },
    { name: 'Clients', path: '/clients' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass shadow-lg' : 'bg-white/95'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 logo-hover" data-testid="logo-link">
            <img
              src={IMAGES.logo}
              alt="Thesaurus of Finance"
              className="h-12 w-auto"
            />
            <div className="hidden sm:block">
              <span className="font-heading text-lg font-semibold text-[#0f172a] leading-tight block">
                Thesaurus of Finance
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              link.hasDropdown ? (
                <div
                  key={link.path}
                  className="relative"
                  onMouseEnter={() => setSubsidiesOpen(true)}
                  onMouseLeave={() => setSubsidiesOpen(false)}
                >
                  <button
                    data-testid="nav-subsidies"
                    className={`nav-link font-medium text-sm tracking-wide transition-colors flex items-center gap-1 ${
                      isActive('/subsidies')
                        ? 'text-[#00bcd4]'
                        : 'text-[#0f172a] hover:text-[#3b5998]'
                    }`}
                  >
                    {link.name}
                    <ChevronDown size={16} className={`transition-transform ${subsidiesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <AnimatePresence>
                    {subsidiesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-slate-100 overflow-hidden"
                        data-testid="subsidies-dropdown"
                      >
                        <div className="p-2">
                          {subsidySchemes.map((scheme) => (
                            <Link
                              key={scheme.path}
                              to={scheme.path}
                              data-testid={`nav-${scheme.name.toLowerCase().replace(/\s+/g, '-')}`}
                              className="block px-4 py-3 rounded-lg hover:bg-[#f8fafc] transition-colors group"
                            >
                              <span className="font-medium text-[#0f172a] group-hover:text-[#3b5998] text-sm block">
                                {scheme.name}
                              </span>
                              <span className="text-xs text-[#64748b]">{scheme.description}</span>
                            </Link>
                          ))}
                        </div>
                        <div className="bg-[#f8fafc] px-4 py-3 border-t border-slate-100">
                          <Link
                            to="/subsidies"
                            className="text-sm font-medium text-[#3b5998] hover:text-[#00bcd4] transition-colors"
                          >
                            View All Schemes →
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  className={`nav-link font-medium text-sm tracking-wide transition-colors ${
                    isActive(link.path)
                      ? 'text-[#00bcd4]'
                      : 'text-[#0f172a] hover:text-[#3b5998]'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
            <a
              href="tel:+917791093252"
              data-testid="nav-phone"
              className="flex items-center gap-2 gradient-btn px-5 py-2.5 rounded-full text-sm font-medium"
            >
              <Phone size={16} />
              <span>+91-77910-93252</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-[#0f172a]"
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-t border-slate-200 max-h-[80vh] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-2">
              {navLinks.filter(l => !l.hasDropdown).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  className={`block py-2 font-medium ${
                    isActive(link.path)
                      ? 'text-[#00bcd4]'
                      : 'text-[#0f172a]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile Subsidies Section */}
              <div className="py-2">
                <p className="font-medium text-[#0f172a] mb-2">Subsidies</p>
                <div className="pl-4 space-y-1">
                  {subsidySchemes.map((scheme) => (
                    <Link
                      key={scheme.path}
                      to={scheme.path}
                      onClick={() => setIsOpen(false)}
                      className="block py-1.5 text-sm text-[#64748b] hover:text-[#00bcd4]"
                    >
                      {scheme.name}
                    </Link>
                  ))}
                </div>
              </div>
              
              <a
                href="tel:+917791093252"
                className="flex items-center justify-center gap-2 gradient-btn px-5 py-3 rounded-full text-sm font-medium mt-4"
              >
                <Phone size={16} />
                <span>+91-77910-93252</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
